import {AfterViewInit, ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {CreateUpdateComponent} from '../../../../admin-shared/crud-components/create-update/create-update.component';
import {translations} from '../../../../../environments/translations.ru';
import {AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {map, takeUntil, tap} from 'rxjs/operators';
import {
  Alphabet,
  CharacterCase,
  FeatureType,
  FeatureTypes,
  IdKeyAndHeader,
  StringCustomValidatorContent,
  StringValidatorType,
  Validator
} from 'compob2b-libs';
import {CustomValidators} from '../../../../admin-shared/custom-validators/CustomValidators';
import {StringValidator} from '../../../../admin-shared/custom-validators/StringValidator';
import {Observable} from 'rxjs';
import {HttpParams} from '@angular/common/http';
import {NbButtonModule, NbCardModule, NbIconModule, NbInputModule, NbSpinnerModule, NbToggleModule, NbTooltipModule} from '@nebular/theme'
import {RouterLink} from '@angular/router'
import {NgClass, NgIf} from '@angular/common'
import {NgOptionTemplateDirective, NgSelectComponent} from '@ng-select/ng-select'

interface ValidatorFeatureType {
  featureTypeId: number | null;
  multiple: boolean;
}

@Component({
  templateUrl: './validator-create-update.component.html',
  styleUrls: ['validator-create-update.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbSpinnerModule,
    RouterLink,
    NgClass,
    ReactiveFormsModule,
    NbIconModule,
    NgIf,
    NbButtonModule,
    NgSelectComponent,
    NbToggleModule,
    NgOptionTemplateDirective,
    NbInputModule,
    NbTooltipModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ValidatorCreateUpdateComponent extends CreateUpdateComponent<Validator> implements OnInit, AfterViewInit {
  static supportedTypes: number[] = [
    FeatureTypes.SELECT,
    FeatureTypes.CHECKBOX,
    FeatureTypes.STRING,
    FeatureTypes.HTML,
    FeatureTypes.RANGE,
    FeatureTypes.INTERVAL,
  ];

  static symbols: IdKeyAndHeader[] = [
    {id: ' ', header: translations.spaceSymbol} as IdKeyAndHeader,
    {id: '_', header: translations.underscoreSymbol} as IdKeyAndHeader,
    {id: '.', header: translations.pointSymbol} as IdKeyAndHeader,
    {id: ',', header: translations.commaSymbol} as IdKeyAndHeader,
  ];

  static alphabets: IdKeyAndHeader[] = [
    {id: Alphabet.CYRILLIC, header: translations.cyrillicAlphabet} as IdKeyAndHeader,
    {id: Alphabet.LATIN, header: translations.latinAlphabet} as IdKeyAndHeader,
  ];

  static characterCases: IdKeyAndHeader[] = [
    {id: CharacterCase.UPPERCASE, header: translations.characterUpperCase} as IdKeyAndHeader,
    {id: CharacterCase.LOWERCASE, header: translations.characterLowerCase} as IdKeyAndHeader,
  ];

  title = translations.validatorsTitle;
  absolutePath = '/cabinet/pim/validators';
  uri = '/validator';
  redirectToEditPage = true;
  showRegexpFormField = false;
  showSymbolsFormField = false;
  showCharactersFieldsFormField = false;
  showMultipleSelectFormField = false;

  showMultipleSelectValidators = false;
  showStringValidators = false;
  showCustomStringValidators = false;
  showHtmlValidators = false;
  showNumberValidators = false;
  showIntervalValidators = false;

  loadingFeatureTypes = true;
  featureTypes: FeatureType[] = [];

  stringValidatorTypes: { id: string; header: string }[] = [
    {id: StringValidatorType.EMAIL, header: 'Email'},
    {id: StringValidatorType.URL, header: 'URL'},
    {id: StringValidatorType.CUSTOM, header: 'Кастомный тип'},
  ];

  form: FormGroup = new FormGroup({
    header: new FormControl(null, Validators.required, this.uniqueHeaderValidator()),
    example: new FormControl(null, Validators.required),
    featureType: new FormGroup({
      featureTypeId: new FormControl(null, Validators.required),
      multiple: new FormControl(null),
    }, this.useMultipleSelectValidator),

    content: new FormGroup({
      multipleSelectValidator: new FormGroup({
        minValuesNumber: new FormControl(null, Validators.min(0)),
        maxValuesNumber: new FormControl(null, Validators.min(1)),
      }, CustomValidators.minMaxValueGroupValidator('minValuesNumber', 'maxValuesNumber', true)),

      numberValidator: new FormGroup({
        isInteger: new FormControl(false),
        minValue: new FormControl(null),
        maxValue: new FormControl(null),
      }, CustomValidators.minMaxValueGroupValidator('minValue', 'maxValue')),

      stringValidator: new FormGroup({
        typeValidator: new FormControl(null, Validators.required),
        customValidator: new FormGroup({
          minLength: new FormControl(null, Validators.min(0)),
          maxLength: new FormControl(null, Validators.min(1)),
          useDigits: new FormControl(false),
          useCharacters: new FormControl(false),
          characters: new FormGroup({
            alphabets: new FormControl([], Validators.required),
            charactersCases: new FormControl([], Validators.required),
          }),
          useSymbols: new FormControl(false),
          symbols: new FormControl([]),
          useRegexp: new FormControl(false),
          regexp: new FormControl(null),
        }),
      }, this.stringGroupValidator),

      htmlValidator: new FormGroup({
        minLength: new FormControl(null, Validators.min(0)),
        maxLength: new FormControl(null, Validators.min(1)),
      }, CustomValidators.minMaxValueGroupValidator('minLength', 'maxLength', true)),

      intervalValidator: new FormGroup({
        isInteger: new FormControl(false),
        lowerBoundary: new FormGroup({
          minValue: new FormControl(null),
          maxValue: new FormControl(null),
        }, CustomValidators.minMaxValueGroupValidator('minValue', 'maxValue')),
        upperBoundary: new FormGroup({
          minValue: new FormControl(null),
          maxValue: new FormControl(null),
        }, CustomValidators.minMaxValueGroupValidator('minValue', 'maxValue')),
      }),
    }),
  });

  uniqueHeaderValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
      let params = new HttpParams().set('header', control.value);
      if (this.editMode) {
        params = params.set('updatedEntityId', this.id);
      }

      const uri = this.uri + '/is-header-unique';

      return this.compoApiService.getWithParams<boolean>(uri, params)
        .pipe(
          map(response => response.data),
          map(item => item ? null : {notUnique: true}),
          tap(_ => this.cdr.detectChanges())
        );
    };
  }

  useMultipleSelectValidator(control: AbstractControl): ValidationErrors | null {
    const featureTypeId: number | null = control.get('featureTypeId').value;
    const multipleSelect: boolean = control.get('multiple').value;

    if (featureTypeId === FeatureTypes.SELECT && multipleSelect === false) {
      return {useMultipleSelect: true};
    }

    return null;
  }

  stringGroupValidator(control: AbstractControl): ValidationErrors | null {
    const typeValidator: string | null = control.get('typeValidator').value;
    const minLength: number | null = control.get('customValidator.minLength').value;
    const maxLength: number | null = control.get('customValidator.maxLength').value;
    const useDigits: boolean = control.get('customValidator.useDigits').value;
    const useCharacters: boolean = control.get('customValidator.useCharacters').value;
    const useSymbols: boolean = control.get('customValidator.useSymbols').value;
    const regexp: string | null = control.get('customValidator.regexp').value;

    if (typeValidator === 'CUSTOM') {
      const minMaxValuesError
        = CustomValidators.minMaxValueGroupValidator('customValidator.minLength', 'customValidator.maxLength')(control);
      if (minMaxValuesError !== null) {
        return minMaxValuesError;
      }

      if (minLength === null && maxLength === null
        && useDigits === false && useCharacters === false && useSymbols === false
        && (regexp === null || regexp.trim() === '')) {
        return {validatorEmpty: true};
      }
    }

    return null;
  }

  ngOnInit(): void {
    this.form.statusChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.cdr.markForCheck());

    this.config = {
      uri: this.uri,
      columns: [],
      title: this.title,
      absolutePath: this.absolutePath,
      relatedEntity: [],
    };

    super.setConfig(this.config);
    super.ngOnInit();

    this.compoApiService.get<FeatureType[]>('/feature-type/list')
      .pipe(
        map(response => response.data),
        takeUntil(this.unsubscribe)
      )
      .subscribe(types => this.handleSuccessLoadingFeatureTypes(types));

    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(value => this.manageExampleValidators(value));

    this.form.get('content.stringValidator.customValidator.useSymbols').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((useSymbols: boolean): void => this.handleUseSymbolsValueChange(useSymbols));

    this.form.get('content.stringValidator.customValidator.useCharacters').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((useCharacters: boolean): void => this.handleUseCharactersValueChange(useCharacters));

    this.form.get('content.stringValidator.typeValidator').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((type: string | null): void => this.handleTypeValidatorValueChange(type));

    this.form.get('content.stringValidator.customValidator.useRegexp').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((useRegexp: boolean): void => this.handleUseRegexpValueChange(useRegexp));

    this.featureType.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((featureType: ValidatorFeatureType): void => this.handleFeatureTypeValueChange(featureType));

    if (this.editMode) {
      this.featureType.disable();
    }
  }

  private manageExampleValidators(value): void {
    this.example.clearValidators();
    const featureTypeId: number | null | undefined = this.editMode && this.featureType.disabled
      ? this.model?.featureTypeId
      : value?.featureType?.featureTypeId;
    if (featureTypeId === null || featureTypeId === undefined
      || ![FeatureTypes.STRING, FeatureTypes.RANGE, FeatureTypes.INTERVAL].includes(featureTypeId)) {
      return;
    }

    if (featureTypeId === FeatureTypes.RANGE && this.form.get('content.numberValidator').valid) {
      this.example.addValidators(
        [
          Validators.required,
          CustomValidators.numberValueValidator(this.form.get('content.numberValidator').value)]
      );
      this.example.updateValueAndValidity({emitEvent: false});
    }

    if (featureTypeId === FeatureTypes.INTERVAL && this.form.get('content.intervalValidator').valid) {
      this.example.addValidators(
        [
          Validators.required,
          CustomValidators.intervalValueValidator(this.form.get('content.intervalValidator').value)]
      );
      this.example.updateValueAndValidity({emitEvent: false});
    }

    if (featureTypeId === FeatureTypes.STRING && this.form.get('content.stringValidator').valid) {
      this.example.addValidators(
        [
          Validators.required,
          StringValidator.getValidator(this.form.get('content.stringValidator').value)]
      );
      this.example.updateValueAndValidity({emitEvent: false});
    }
  }

  private handleSuccessLoadingFeatureTypes(types: FeatureType[]): void {
    this.featureTypes = types.filter(type => ValidatorCreateUpdateComponent.supportedTypes.includes(type.id));
    this.loadingFeatureTypes = false;
    this.cdr.detectChanges();
  }

  private handleUseSymbolsValueChange(useSymbols: boolean): void {
    const symbolsValidator = this.form.get('content.stringValidator.customValidator.symbols');
    if (useSymbols === true) {
      symbolsValidator.enable();
      symbolsValidator.addValidators(Validators.required);
      symbolsValidator.setValue(ValidatorCreateUpdateComponent.symbols.map(item => item.id));
      this.showSymbolsFormField = true;
    }
    else {
      symbolsValidator.disable();
      symbolsValidator.removeValidators(Validators.required);
      symbolsValidator.reset();
      this.showSymbolsFormField = false;
    }
  }

  private handleUseCharactersValueChange(useCharacters: boolean): void {
    if (useCharacters === true) {
      this.form.get('content.stringValidator.customValidator.characters').enable();
      this.form.get('content.stringValidator.customValidator.characters.alphabets')
        .setValue(ValidatorCreateUpdateComponent.alphabets.map(item => item.id));
      this.form.get('content.stringValidator.customValidator.characters.charactersCases')
        .setValue(ValidatorCreateUpdateComponent.characterCases.map(item => item.id));
      this.showCharactersFieldsFormField = true;
    }
    else {
      this.form.get('content.stringValidator.customValidator.characters').disable();
      this.form.get('content.stringValidator.customValidator.characters').reset();
      this.showCharactersFieldsFormField = false;
    }
  }

  private handleTypeValidatorValueChange(type: string | null): void {
    this.showCustomStringValidators = false;
    this.form.get('content.stringValidator.customValidator').reset();
    if (type === 'CUSTOM') {
      this.showCustomStringValidators = true;
    }
  }

  private handleUseRegexpValueChange(useRegexp: boolean): void {
    if (useRegexp === true) {
      this.form.get('content.stringValidator.customValidator.minLength').reset();
      this.form.get('content.stringValidator.customValidator.maxLength').reset();
      this.form.get('content.stringValidator.customValidator.useDigits').reset();
      this.form.get('content.stringValidator.customValidator.useCharacters').reset();
      this.form.get('content.stringValidator.customValidator.useSymbols').reset();
      this.form.get('content.stringValidator.customValidator.characters').reset();
      this.form.get('content.stringValidator.customValidator.symbols').reset();

      this.form.get('content.stringValidator.customValidator.regexp').enable();
      this.form.get('content.stringValidator.customValidator.regexp').addValidators(Validators.required);
      this.showRegexpFormField = true;
    }
    else {
      this.form.get('content.stringValidator.customValidator.regexp').disable();
      this.form.get('content.stringValidator.customValidator.regexp').reset();
      this.form.get('content.stringValidator.customValidator.regexp').removeValidators(Validators.required);
      this.showRegexpFormField = false;
    }
  }

  private handleFeatureTypeValueChange(featureType: ValidatorFeatureType): void {
    const featureTypeId: number | null = featureType.featureTypeId;
    const multipleSelect: boolean = featureType.multiple;

    this.showRegexpFormField = false;
    this.showMultipleSelectFormField = false;
    this.showMultipleSelectValidators = false;
    this.showStringValidators = false;
    this.showHtmlValidators = false;
    this.showCustomStringValidators = false;
    this.showNumberValidators = false;
    this.showIntervalValidators = false;

    this.content.reset();
    this.content.disable();
    this.example.disable();

    if (featureTypeId === null) {
      return;
    }

    if (featureTypeId === FeatureTypes.SELECT) {
      this.showMultipleSelectFormField = true;
    }
    else {
      this.form.get('featureType.multiple').setValue(null, {emitEvent: false});
    }

    if ((featureTypeId === FeatureTypes.SELECT && multipleSelect) || featureTypeId === FeatureTypes.CHECKBOX) {
      this.showMultipleSelectValidators = true;
      this.form.get('content.multipleSelectValidator').enable();
    }

    if (featureTypeId === FeatureTypes.STRING) {
      this.showStringValidators = true;
      this.form.get('content.stringValidator').enable();
      this.form.get('example').enable();
    }

    if (featureTypeId === FeatureTypes.HTML) {
      this.showHtmlValidators = true;
      this.form.get('content.htmlValidator').enable();
    }

    if (featureTypeId === FeatureTypes.RANGE) {
      this.showNumberValidators = true;
      this.form.get('content.numberValidator').enable();
      this.form.get('example').enable();
    }

    if (featureTypeId === FeatureTypes.INTERVAL) {
      this.showIntervalValidators = true;
      this.form.get('content.intervalValidator').enable();
      this.form.get('example').enable();
    }
  }

  beforeSubmit(): void {
    const validator = this.form.value;
    this.entity = {
      id: this.editMode ? parseInt(this.id, 10) : null,
      header: validator.header,
      featureTypeId: this.editMode ? this.model.featureTypeId : validator.featureType.featureTypeId,
      multiple: this.editMode ? this.model.multiple : validator.featureType.multiple,
      content: validator.content,
      example: validator.example,
    } as Validator;
  }

  ngAfterViewInit() {
    if (!this.editMode) {
      this.loading = false;
    }
  }

  get header(): AbstractControl {
    return this.form.get('header');
  }

  get example(): AbstractControl {
    return this.form.get('example');
  }

  get featureTypeId(): AbstractControl {
    return this.form.get('featureType.featureTypeId');
  }

  get featureType(): AbstractControl {
    return this.form.get('featureType');
  }

  get content(): AbstractControl {
    return this.form.get('content');
  }

  get stringCustomValidatorValue(): StringCustomValidatorContent {
    return this.form.get('content.stringValidator.customValidator').value;
  }

  afterSubmit() {
    if (this.redirectToEditPage && this.createdId !== undefined) {
      this.router.navigate(['cabinet', 'pim', 'validators', 'edit', this.createdId]);
    }
    else {
      this.afterSetModel();
      this.cdr.detectChanges();
    }
  }

  isNumberFeatureType(): boolean {
    const featureTypeId: number = this.featureTypeId.value;
    return featureTypeId === FeatureTypes.RANGE;
  }

  isIntervalFeatureType(): boolean {
    const featureTypeId: number = this.featureTypeId.value;
    return featureTypeId === FeatureTypes.INTERVAL;
  }

  protected setModel(model: Validator): void {
    this.model = model;
    this.form.patchValue(model);
    this.featureType.setValue({featureTypeId: model.featureTypeId, multiple: model.multiple});
    this.form.get('content').patchValue(model.content);
    this.deleted = this.model.deleted;
    this.afterSetModel();
    this.loading = false;
    this.cdr.detectChanges();
  }

  getSymbols(): IdKeyAndHeader[] {
    return ValidatorCreateUpdateComponent.symbols;
  }

  getAlphabets(): IdKeyAndHeader[] {
    return ValidatorCreateUpdateComponent.alphabets;
  }

  getCharacterCases(): IdKeyAndHeader[] {
    return ValidatorCreateUpdateComponent.characterCases;
  }
}
